// Libs
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Local
import CreateEmailCampaignPage from "./CreateEmailCampaignPage";
import {
  ExistingEmailTemplate,
  EmailTemplateStatus,
} from "../../models/email-template";
import { DbRead } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";
import { EMAIL_CAMPAIGNS_URL } from "../../urls";
import { ExistingEmailCampaign } from "../../models/email-campaign";
import { CSVCustomerRecord } from "../../components/EmailMarketing/CSVCustomerUploader";
import { ExistingCustomer } from "../../models/customer";

interface Props {
  siteKey: string;
}

export default function CreateEmailCampaignContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedTemplateId = searchParams.get("templateId");

  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<ExistingEmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [campaigns, setCampaigns] = useState<ExistingEmailCampaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  useEffect(() => {
    // Subscribe to real-time updates of email templates
    const unsubscribeTemplates = DbRead.emailTemplates.subscribe({
      siteKey,
      onChange: (templatesList) => {
        setTemplates(templatesList);
        setLoadingTemplates(false);
      },
      onError: (error) => {
        console.error("Error loading templates:", error);
        addToastMessage({
          id: `templates-error-${Date.now()}`,
          message: "Failed to load email templates",
          type: "error",
          dialog: false,
        });
        setLoadingTemplates(false);
      },
    });

    return () => {
      unsubscribeTemplates();
    };
  }, [siteKey, addToastMessage]);

  useEffect(() => {
    DbRead.emailCampaigns
      .getAll(siteKey)
      .then((campaignsList) => {
        setCampaigns(campaignsList);
        setLoadingCampaigns(false);
      })
      .catch((error) => {
        console.error("Error loading campaigns:", error);
        setLoadingCampaigns(false);
      });
  }, [siteKey]);

  const handleCreateCampaign = async (campaignData: {
    templateID: string;
    campaignName: string;
    description?: string;
    customers?: ExistingCustomer[];
    csvData?: CSVCustomerRecord[];
  }) => {
    setLoading(true);
    try {
      const response = await DbRead.emailCampaigns.create({
        siteKey,
        templateID: campaignData.templateID,
        campaignName: campaignData.campaignName,
        description: campaignData.description,
        customers: campaignData.customers,
        csvData: campaignData.csvData,
      });

      addToastMessage({
        id: `campaign-created-${Date.now()}`,
        message: `Email campaign created successfully. ${response.totalRecipients} emails queued for sending.`,
        type: "success",
        dialog: false,
      });

      // Navigate to the email campaigns list where the user can see the progress
      // Alternatively, you could navigate to a specific campaign detail page:
      // navigate(`${EMAIL_CAMPAIGNS_URL}/${response.campaignID}`);
      navigate(EMAIL_CAMPAIGNS_URL);
    } catch (error: any) {
      console.error("Error creating email campaign:", error);
      addToastMessage({
        id: `campaign-error-${Date.now()}`,
        message: error.message || "Failed to create email campaign",
        type: "error",
        dialog: false,
      });

      // Re-throw the error so the page component can handle staying on current step
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(EMAIL_CAMPAIGNS_URL);
  };

  const handleUpdateTemplateStatus = async (
    templateId: string,
    status: EmailTemplateStatus,
  ) => {
    try {
      await DbRead.emailTemplates.update({
        siteKey,
        templateID: templateId,
        status: status,
      });

      addToastMessage({
        id: `template-status-updated-${Date.now()}`,
        message: "Template status updated successfully",
        type: "success",
        dialog: false,
      });
    } catch (error: any) {
      console.error("Error updating template status:", error);
      addToastMessage({
        id: `template-status-error-${Date.now()}`,
        message: error.message || "Failed to update template status",
        type: "error",
        dialog: false,
      });
      throw error;
    }
  };

  return (
    <CreateEmailCampaignPage
      siteKey={siteKey}
      loading={loading}
      templates={templates}
      loadingTemplates={loadingTemplates}
      onCreateCampaign={handleCreateCampaign}
      onCancel={handleCancel}
      onUpdateTemplateStatus={handleUpdateTemplateStatus}
      preSelectedTemplateId={preSelectedTemplateId}
      campaigns={campaigns}
      loadingCampaigns={loadingCampaigns}
    />
  );
}
